/**
 * Get date range for analytics views API calls.
 * Ensures `from` is always at 00:00:00.000 and `to` is always at 23:59:59.999.
 *
 * @param {string} selectedDateFilter - The filter value (Today, ThisWeek, ThisMonth, etc.)
 * @param {Date} startDate - Custom start date for "Between" filter
 * @param {Date} endDate - Custom end date for "Between" filter
 * @returns {{ from: string, to: string }} ISO strings for from and to dates
 */
export const getAnalyticsDateRange = (
  selectedDateFilter,
  startDate,
  endDate,
) => {
  const now = new Date();
  let from, to;

  // Helper to set start of day (00:00:00.000)
  const startOfDay = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Helper to set end of day (23:59:59.999)
  const endOfDay = (date) => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
  };

  switch (selectedDateFilter) {
    case "allData": {
      // All data from startDate (campaign creation) to now
      from = startOfDay(startDate || new Date("2023-08-01"));
      to = endOfDay(now);
      break;
    }
    case "Today":
      from = startOfDay(now);
      to = endOfDay(now);
      break;

    case "ThisWeek": {
      // Monday of current week
      const dayOfWeek = now.getDay();
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Monday start
      const monday = new Date(now);
      monday.setDate(now.getDate() - diff);
      from = startOfDay(monday);
      to = endOfDay(now);
      break;
    }

    case "ThisMonth":
      from = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1));
      to = endOfDay(now);
      break;

    case "7days": {
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(now.getDate() - 7);
      from = startOfDay(sevenDaysAgo);
      to = endOfDay(now);
      break;
    }

    case "LastMonth": {
      const firstDayLastMonth = new Date(
        now.getFullYear(),
        now.getMonth() - 1,
        1,
      );
      const lastDayLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      from = startOfDay(firstDayLastMonth);
      to = endOfDay(lastDayLastMonth);
      break;
    }

    case "30days": {
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(now.getDate() - 30);
      from = startOfDay(thirtyDaysAgo);
      to = endOfDay(now);
      break;
    }

    case "90days": {
      const ninetyDaysAgo = new Date(now);
      ninetyDaysAgo.setDate(now.getDate() - 90);
      from = startOfDay(ninetyDaysAgo);
      to = endOfDay(now);
      break;
    }

    case "ThisYear":
      from = startOfDay(new Date(now.getFullYear(), 0, 1));
      to = endOfDay(now);
      break;

    case "LastYear": {
      const lastYear = now.getFullYear() - 1;
      from = startOfDay(new Date(lastYear, 0, 1));
      to = endOfDay(new Date(lastYear, 11, 31));
      break;
    }

    case "Between":
      from = startOfDay(startDate || now);
      to = endOfDay(endDate || now);
      break;

    default:
      throw new Error(`Invalid filter value: ${selectedDateFilter}`);
  }

  return {
    from: from.toISOString(),
    to: to.toISOString(),
  };
};
